import {
  init,
  get,
  set,
  subscribe,
  login,
  onAuthStateChanged,
  logout
} from '@tarobase/js-sdk';
import { useEffect, useState } from 'react';
import {
  ChakraProvider,
  Box,
  Container,
  Heading,
  Text,
  Button,
  Input,
  Stack,
  useToast,
  Image,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Flex,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TarobaseConfiguration from './TarobaseConfiguration.png';
import TarobaseLogo from './logo.png';
import { Global } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';

const DEMO_APP_ID = '67341766bfc2b6d1c6ba2713'; // Replace with your actual app ID

function TarobaseDemo() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [gotNewData, setGotNewData] = useState(false);
  const toast = useToast();
  const isMobileView = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    init({
      appId: DEMO_APP_ID,
    });

    onAuthStateChanged((user) => {
      setLoading(false);
      setUser(user);
    });

    const getMessages = async () => {
      const unsubscribe = await subscribe('messages', {
        prompt: "Limit results to 10 sorted DESC by lastBlockTimestamp",
        onData: (data) => {
          if (data) {
            setGotNewData(true);
            setMessages(Object.entries(data).map(([id, message]) => ({ id, ...message })));
          }
          setLoadingMessages(false);
          setSendingMessage(false);
        },
        onError: (e) => {
          console.error("Failed to get messages", e.message);
          setLoadingMessages(false);
        }
      });

      return unsubscribe;
    };

    getMessages();

  }, []);

  const handleLogin = async () => {
    try {
      setLoading(true);
      await login();
    } catch (error) {
      setLoading(false); // Make sure to set loading to false on error
      toast({
        title: 'Login Failed',
        description: 'Please ensure pop-ups are enabled for wallet connection.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleSubmitMessage = async () => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      setGotNewData(false);
      await set('messages', {
        message: newMessage,
        createdBy: user.address,
      });

      while (!gotNewData) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      setNewMessage('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit message',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleLogout = async () => {
    logout();
    // Reset loading state just in case
    setLoading(false);
  };

  return (
    <ChakraProvider>
      <Global
        styles={`
          @import url('https://fonts.googleapis.com/css2?family=ABeeZee&display=swap');
        `}
      />
      <Box bg="gray.50" minH="100vh" py={8}>
        <Container maxW="container.xl">
          {/* Header Section */}
          <Stack spacing={6} mb={8}>
            <Stack spacing={1}>
              <Heading color="rgb(92, 67, 94)">On-chain Guestbook</Heading>
              <Flex align="center" gap={1}>
                <Text fontSize="xl" fontWeight="semibold">Built with</Text>
                <Image src={TarobaseLogo} alt="Tarobase Logo" height="24px" />
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  fontFamily="'ABeeZee', sans-serif"
                  color="rgb(92, 67, 94)"
                >
                  tarobase
                </Text>
              </Flex>
            </Stack>

            <Card>
              <CardBody>
                <Text>
                  This is a guestbook where you can connect your wallet and leave a message and every message is stored on-chain.
                </Text>
              </CardBody>
            </Card>
          </Stack>

          {isMobileView ? (
            <Alert
              status="warning"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Text mt={4} mb={1} fontSize="lg">
                Mobile View Not Optimized
              </Text>
              <Text fontSize="sm">
                Please visit this demo from a desktop or larger screen for the best experience.
              </Text>
            </Alert>
          ) : (
            <Flex gap={8}>
              {/* Left Column - Instructions */}
              <Box w="40%" p={6} bg="white" borderRadius="lg" boxShadow="sm">
                <Stack spacing={6}>
                  <Heading size="md">How it works</Heading>

                  <Tabs variant="enclosed" size="sm">
                    <TabList>
                      <Tab>JS SDK</Tab>
                      <Tab>Console</Tab>
                    </TabList>

                    <TabPanels>
                      <TabPanel p={4}>
                        <Stack spacing={6}>
                          <Box>
                            <Text fontWeight="bold" mb={2} fontSize="sm">1. Initialize Tarobase</Text>
                            <SyntaxHighlighter
                              language="typescript"
                              style={oneDark}
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.375rem',
                                fontSize: '12px',
                              }}
                            >
                              {`init({
  appId: '${DEMO_APP_ID}'
});`}
                            </SyntaxHighlighter>
                          </Box>

                          <Box>
                            <Text fontWeight="bold" mb={2} fontSize="sm">2. Handle Authentication</Text>
                            <SyntaxHighlighter
                              language="typescript"
                              style={oneDark}
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.375rem',
                                fontSize: '12px',
                              }}
                            >
                              {`await login();
onAuthStateChanged((user) => {
  setUser(user);
});`}
                            </SyntaxHighlighter>
                          </Box>

                          <Box>
                            <Text fontWeight="bold" mb={2} fontSize="sm">3. Store Messages</Text>
                            <SyntaxHighlighter
                              language="typescript"
                              style={oneDark}
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.375rem',
                                fontSize: '12px',
                              }}
                            >
                              {`await set('messages', {
  message: "Hello world",
  createdBy: user.address
});`}
                            </SyntaxHighlighter>
                          </Box>

                          <Box>
                            <Text fontWeight="bold" mb={2} fontSize="sm">4. Subscribe to Updates</Text>
                            <SyntaxHighlighter
                              language="typescript"
                              style={oneDark}
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.375rem',
                                fontSize: '12px',
                              }}
                            >
                              {`// Subscribe to messages with natural language prompts
subscribe('messages', {
  prompt: "Limit results to 10 sorted DESC by lastBlockTimestamp",
  onData: (data) => {
    setMessages(data);
  }
});`}
                            </SyntaxHighlighter>
                          </Box>
                        </Stack>
                      </TabPanel>

                      <TabPanel p={4}>
                        <Stack spacing={6}>
                          <Box>
                            <Text fontWeight="bold" mb={2} fontSize="sm">1. Configure Your App's Settings</Text>
                            <Text fontSize="xs" color="gray.600" mb={2}>
                              In the Tarobase Console, you can configure your app's authentication method and supported chains.
                            </Text>
                            <Box
                              borderRadius="md"
                              overflow="hidden"
                              border="1px solid"
                              borderColor="gray.200"
                            >
                              <img
                                src={TarobaseConfiguration}
                                alt="Tarobase app configuration interface"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block'
                                }}
                              />
                            </Box>
                          </Box>

                          <Box>
                            <Text fontWeight="bold" mb={2} fontSize="sm">2. Configure Collection Policy</Text>
                            <Text fontSize="xs" color="gray.600" mb={2}>
                              A Tarobase policy is used to automatically compile and deploy smart contracts that manage the guestbook's message storage and access control:
                            </Text>
                            <SyntaxHighlighter
                              language="json"
                              style={oneDark}
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.375rem',
                                fontSize: '12px',
                              }}
                            >
                              {`{
  "messages/$messageId": {
    "onchain": true,
    "rules": {
      "write": "@prevState == null && @newState.message != null && @newState.createdBy == @walletAddress",
      "read": "true"
    },
    "fields": {
      "createdBy": "Types.Address",
      "message": "Types.String"
    }
  }
}`}
                            </SyntaxHighlighter>
                            <Stack spacing={1} mt={2}>
                              <Text fontSize="xs" color="gray.600">
                                This policy configuration:
                              </Text>
                              <Text fontSize="xs" color="gray.600">• Ensures messages are stored on-chain</Text>
                              <Text fontSize="xs" color="gray.600">• Only allows creating new messages (no updates)</Text>
                              <Text fontSize="xs" color="gray.600">• Requires the message creator to be the signer</Text>
                              <Text fontSize="xs" color="gray.600">• Allows public read access</Text>
                            </Stack>
                          </Box>
                        </Stack>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Stack>
              </Box>

              {/* Right Column - App */}
              <Box w="60%" p={6} bg="white" borderRadius="lg" boxShadow="sm">
                {user ? (
                  <Stack spacing={4}>
                    <Flex justify="space-between" align="center">
                      <Text>
                        Connected as: {user.address.slice(0, 6)}...
                        {user.address.slice(-4)}
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        color="gray.500"
                        onClick={handleLogout}
                      >
                        disconnect
                      </Button>
                    </Flex>
                    <Stack direction="row">
                      <Input
                        placeholder="Leave a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={sendingMessage}
                      />
                      <Button
                        bg="rgb(92, 67, 94)"
                        color="white"
                        _hover={{ bg: 'rgb(112, 82, 114)' }}
                        onClick={handleSubmitMessage}
                        isLoading={sendingMessage}
                        loadingText="Signing"
                      >
                        Post
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={4}>
                    <Alert status="info">
                      <AlertIcon />
                      Please enable pop-ups in your browser for wallet connection if prompted
                    </Alert>
                    <Button
                      bg="rgb(92, 67, 94)"
                      color="white"
                      _hover={{ bg: 'rgb(112, 82, 114)' }}
                      onClick={handleLogin}
                      isLoading={loading}
                    >
                      Connect Wallet to Sign Guestbook
                    </Button>
                  </Stack>
                )}

                <Stack spacing={4} mt={6}>
                  <Heading size="md">Recent Guestbook Entries</Heading>

                  {loadingMessages ? (
                    <Card>
                      <CardBody>
                        <Text>Loading messages...</Text>
                      </CardBody>
                    </Card>
                  ) : messages.length === 0 ? (
                    <Text>No messages yet</Text>
                  ) : (
                    messages.map((msg, index) => (
                      <Card key={index}>
                        <CardBody>
                          <Text fontSize="sm" color="gray.500" mb={2}>
                            {msg.createdBy} wrote:
                          </Text>
                          <Text>{msg.message}</Text>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            {new Date(msg.lastBlockTimestamp * 1000).toLocaleString()}
                          </Text>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </Stack>
              </Box>
            </Flex>
          )}
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default TarobaseDemo;
